import { SportEventStatus } from '@/@types/graphql-types';

type EventSpinningTextInfo = {
	matchKnown: boolean;
	dateKnown: boolean;
	serviceKnown: boolean;
	severalServices: boolean;
	highlightServiceKnown: boolean;
	severalHighlightServices: boolean;
	interviewServiceKnown: boolean;
	severalInterviewServices: boolean;
	replayServiceKnown: boolean;
	severalReplayServices: boolean;
	freeServiceKnown: boolean;
	severalFreeServices: boolean;
	freeHighlightServiceKnown: boolean;
	severalFreeHighlightServices: boolean;
	freeInterviewServiceKnown: boolean;
	severalFreeInterviewServices: boolean;
	freeReplayServiceKnown: boolean;
	severalFreeReplayServices: boolean;
	offerObjectTypes: string[];
};

type EventSpinningTextVars = {
	event?: string;
	match: string;
	sport: string;
	time?: string;
	date?: string;
	service?: string;
	services?: string;
	freeService?: string;
	freeServices?: string;
	highlightService?: string;
	highlightServices?: string;
	freeHighlightService?: string;
	freeHighlightServices?: string;
	interviewService?: string;
	interviewServices?: string;
	freeInterviewService?: string;
	freeInterviewServices?: string;
	replayService?: string;
	replayServices?: string;
	freeReplayService?: string;
	freeReplayServices?: string;
};

// @ts-ignore
const getListFormatter = (language: string) => new Intl.ListFormat(language, { style: 'long', type: 'conjunction' });

export function provideEventSpinningText(
	status: SportEventStatus,
	match: string | null,
	date: string | undefined,
	offers: any
) {
	const info = getSpinningTextInfo(match, date, offers);

	let heading = 'SPORTS_WEBAPP_SPIN_PREMATCH_TITLE_H_0';
	let content: string[] = [];

	switch (status) {
		case SportEventStatus.NotStarted: {
			if (info.matchKnown && info.dateKnown) {
				content.push('SPORTS_WEBAPP_SPIN_PREMATCH_TIMEDATE_0');
			}

			if (info.serviceKnown) {
				content.push('SPORTS_WEBAPP_SPIN_PREMATCH_STREAM_0');
			}

			if (info.severalServices) {
				content.push('SPORTS_WEBAPP_SPIN_PREMATCH_STREAMS_0');
			}
			break;
		}
		case SportEventStatus.Live: {
			if (info.matchKnown && info.dateKnown) {
				content.push('SPORTS_WEBAPP_SPIN_MATCH_TIMEDATE_0');
			}

			if (info.serviceKnown) {
				content.push('SPORTS_WEBAPP_SPIN_MATCH_STREAM_0');
			}

			if (info.severalServices) {
				content.push('SPORTS_WEBAPP_SPIN_MATCH_STREAMS_0');
			}
			break;
		}
		case SportEventStatus.Ended:
		case SportEventStatus.Closed: {
			if (info.matchKnown && info.dateKnown) {
				content.push('SPORTS_WEBAPP_SPIN_POSTMATCH_TIMEDATE_0');
			}

			if (info.offerObjectTypes.includes('REPLAY')) {
				heading = 'SPORTS_WEBAPP_SPIN_REPLAY_TITLE_H_0';

				if (info.severalReplayServices) {
					content.push('SPORTS_WEBAPP_SPIN_REPLAY_STREAMS_0');
				} else if (info.replayServiceKnown) {
					content.push('SPORTS_WEBAPP_SPIN_REPLAY_STREAM_0');
				}

				if (info.severalFreeReplayServices) {
					content.push('SPORTS_WEBAPP_SPIN_REPLAY_FREE_STREAMS_0');
				} else if (info.freeReplayServiceKnown) {
					content.push('SPORTS_WEBAPP_SPIN_REPLAY_FREE_STREAM_0');
				}
			}

			if (info.offerObjectTypes.includes('HIGHLIGHT')) {
				heading = 'SPORTS_WEBAPP_SPIN_HIGHLIGHT_TITLE_H_0';

				if (info.severalHighlightServices) {
					content.push('SPORTS_WEBAPP_SPIN_HIGHLIGHT_STREAMS_0');
				} else if (info.highlightServiceKnown) {
					content.push('SPORTS_WEBAPP_SPIN_HIGHLIGHT_STREAM_0');
				}

				if (info.severalFreeHighlightServices) {
					content.push('SPORTS_WEBAPP_SPIN_HIGHLIGHT_FREE_STREAMS_0');
				} else if (info.freeHighlightServiceKnown) {
					content.push('SPORTS_WEBAPP_SPIN_HIGHLIGHT_FREE_STREAM_0');
				}
			}

			if (info.offerObjectTypes.includes('INTERVIEW')) {
				heading = 'SPORTS_WEBAPP_SPIN_INTERVIEW_TITLE_H_0';

				if (info.severalInterviewServices) {
					content.push('SPORTS_WEBAPP_SPIN_INTERVIEW_STREAMS_0');
				} else if (info.interviewServiceKnown) {
					content.push('SPORTS_WEBAPP_SPIN_INTERVIEW_STREAM_0');
				}

				if (info.severalFreeInterviewServices) {
					content.push('SPORTS_WEBAPP_SPIN_INTERVIEW_FREE_STREAMS_0');
				} else if (info.freeInterviewServiceKnown) {
					content.push('SPORTS_WEBAPP_SPIN_INTERVIEW_FREE_STREAM_0');
				}
			}
			break;
		}
	}

	return { heading, content };
}

const getSpinningTextInfo = (match: string | null, date: string | undefined, offers: any): EventSpinningTextInfo => {
	const spinningTextOffers = getSpinningTextOffers(offers);

	return {
		matchKnown: match != null,
		dateKnown: date != null,
		serviceKnown: spinningTextOffers?.offers ? offers?.[0] != null : false,
		severalServices: spinningTextOffers?.offers ? offers?.length > 1 : false,
		highlightServiceKnown: spinningTextOffers?.highlightOffers?.length === 1,
		severalHighlightServices: spinningTextOffers?.highlightOffers?.length > 1,
		interviewServiceKnown: spinningTextOffers?.interviewOffers?.length === 1,
		severalInterviewServices: spinningTextOffers?.interviewOffers?.length > 1,
		replayServiceKnown: spinningTextOffers?.replayOffers?.length === 1,
		severalReplayServices: spinningTextOffers?.replayOffers?.length > 1,
		freeServiceKnown: spinningTextOffers?.freeOffers?.length === 1,
		severalFreeServices: spinningTextOffers?.freeOffers?.length > 1,
		freeHighlightServiceKnown: spinningTextOffers?.freeHighlightOffers?.length === 1,
		severalFreeHighlightServices: spinningTextOffers?.freeHighlightOffers?.length > 1,
		freeInterviewServiceKnown: spinningTextOffers?.freeInterviewOffers?.length === 1,
		severalFreeInterviewServices: spinningTextOffers?.freeInterviewOffers?.length > 1,
		freeReplayServiceKnown: spinningTextOffers?.freeReplayOffers?.length === 1,
		severalFreeReplayServices: spinningTextOffers?.freeReplayOffers?.length > 1,
		offerObjectTypes: offers?.map((offer: any) => offer.sportObjectType) ?? [],
	};
};

export const getSpinningTextVars = (
	language: string,
	offers: any,
	watchNowOffer: any,
	eventTitle: string,
	sportName: string,
	date: string | undefined,
	time: string | undefined
): EventSpinningTextVars => {
	const listFormatter = getListFormatter(language);
	const concatServices = (offers: any) => listFormatter.format(offers?.map((offer: any) => offer.package.clearName));

	const spinningTextOffers = getSpinningTextOffers(offers);

	return {
		event: eventTitle,
		match: eventTitle,
		sport: sportName,
		date: date,
		time: time,
		service: watchNowOffer?.package?.clearName ?? '',
		services: concatServices(spinningTextOffers?.offers),
		freeService: spinningTextOffers?.freeOffers?.[0]?.package?.clearName ?? '',
		freeServices: concatServices(spinningTextOffers?.freeOffers),
		highlightService: spinningTextOffers?.highlightOffers?.[0]?.package?.clearName ?? '',
		highlightServices: concatServices(spinningTextOffers?.highlightOffers),
		freeHighlightService: spinningTextOffers?.freeHighlightOffers?.[0]?.package.clearName ?? '',
		freeHighlightServices: concatServices(spinningTextOffers?.freeHighlightOffers),
		interviewService: spinningTextOffers?.interviewOffers?.[0]?.package?.clearName ?? '',
		interviewServices: concatServices(spinningTextOffers?.interviewOffers),
		freeInterviewService: spinningTextOffers?.freeInterviewOffers?.[0]?.package?.clearName ?? '',
		freeInterviewServices: concatServices(spinningTextOffers?.freeInterviewOffers),
		replayService: spinningTextOffers?.replayOffers?.[0]?.package?.clearName ?? '',
		replayServices: concatServices(spinningTextOffers?.replayOffers),
		freeReplayService: spinningTextOffers?.freeReplayOffers?.[0]?.package?.clearName ?? '',
		freeReplayServices: concatServices(spinningTextOffers?.freeReplayOffers),
	};
};

const getSpinningTextOffers = (offers: any) => {
	return {
		offers: offers,
		freeOffers: offers?.filter((offer: any) => {
			return offer.monetizationType === 'FREE' || offer.monetizationType === 'ADS';
		}),
		highlightOffers: offers?.filter((offer: any) => {
			return offer.sportObjectType === 'HIGHLIGHT';
		}),
		freeHighlightOffers: offers
			?.filter((offer: any) => {
				return offer.monetizationType === 'FREE' || offer.monetizationType === 'ADS';
			})
			.filter((offer: any) => {
				return offer.sportObjectType === 'HIGHLIGHT';
			}),
		interviewOffers: offers?.filter((offer: any) => {
			return offer.sportObjectType === 'INTERVIEW';
		}),
		freeInterviewOffers: offers
			?.filter((offer: any) => {
				return offer.monetizationType === 'FREE' || offer.monetizationType === 'ADS';
			})
			.filter((offer: any) => {
				return offer.sportObjectType === 'INTERVIEW';
			}),
		replayOffers: offers?.filter((offer: any) => {
			return offer.sportObjectType === 'REPLAY';
		}),
		freeReplayOffers: offers
			?.filter((offer: any) => {
				return offer.monetizationType === 'FREE' || offer.monetizationType === 'ADS';
			})
			.filter((offer: any) => {
				return offer.sportObjectType === 'REPLAY';
			}),
	};
};
